import { PACING_PRESETS, type PacingPreset } from "@/services/api/harnessApi";
import type { UserSettings } from "../useUserSettings";
import { SettingsSection } from "./SettingsSection";

/**
 * ROW 2 — "How often it may act" (D-206-010 row 2, kept-editable).
 *
 * **Who does what:** the person says how often Raisy may wake and work on his company; Raisy wakes
 * on that rhythm instead of whenever it likes.
 * **What he sees:** the rhythm in plain words plus the exact interval, and three choices.
 * **What he feels:** *"my meeting hour is defended"* — Sam's W8 want. A cadence is the difference
 * between a system that works for him and one that interrupts him, which is why the interval is
 * shown as a number rather than hidden behind the preset's name.
 *
 * ⚠️ THE STORE ALREADY EXISTED AND WAS ADMIN-GATED, WHICH IS THE POINT OF THIS ROW: `/harness/pacing`
 * and its update route are built (`harnessApi.getPacing` / `updatePacing`, stored in
 * `entities.preferences['pacing']` — a sibling of `approval_mode`), and the HARNESS nav group is
 * `adminOnly`, so a regular user could not reach a setting that is entirely about him. Nothing was
 * added here except the reachability.
 *
 * A CUSTOM CADENCE IS DELIBERATELY NOT OFFERED IN THIS CONTROL. The BE accepts
 * `wake_cadence_hours` as well, and a stored value can therefore be `preset: "custom"` — which is
 * why the value line states the ACTUAL interval rather than echoing the preset's default, and why a
 * custom interval is reported as custom instead of being silently relabelled as the nearest preset.
 */
export const CadenceSection = ({ settings }: { settings: UserSettings }) => {
  const { pacing, savePacing, pacingSave, pacingSaveError } = settings;

  const presetOf = (p: { preset: PacingPreset | "custom"; wake_cadence_hours: number }) =>
    p.preset === "custom" ? null : PACING_PRESETS[p.preset];

  const value =
    pacing.state === "loading" ? (
      <span className="text-muted-foreground">Reading your rhythm…</span>
    ) : pacing.state === "unavailable" ? (
      <span className="text-muted-foreground">
        Not readable right now — {pacing.reason}. Your rhythm is unchanged.
      </span>
    ) : (
      <span>
        {presetOf(pacing.value)?.label ?? "Your own rhythm"}
        <span className="ml-2 font-normal text-muted-foreground">
          {`wakes about every ${pacing.value.wake_cadence_hours}h`}
          {pacing.value.is_default ? " · the default, nothing chosen yet" : ""}
        </span>
      </span>
    );

  return (
    <SettingsSection
      title="How often it may act"
      hint="The rhythm Raisy wakes on to work on your company."
      value={value}
      saveState={pacingSave}
      saveError={pacingSaveError}
      control={
        pacing.state === "ready" ? (
          <select
            aria-label="How often it may act"
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            value={pacing.value.preset === "custom" ? "" : pacing.value.preset}
            disabled={pacingSave === "saving"}
            onChange={(e) => void savePacing(e.target.value as PacingPreset)}
          >
            {pacing.value.preset === "custom" && (
              <option value="">Your own rhythm ({pacing.value.wake_cadence_hours}h)</option>
            )}
            {(Object.keys(PACING_PRESETS) as PacingPreset[]).map((preset) => (
              <option key={preset} value={preset}>
                {PACING_PRESETS[preset].label} — {PACING_PRESETS[preset].hint}
              </option>
            ))}
          </select>
        ) : undefined
      }
    />
  );
};
